import { useEffect, useRef, useState } from 'react'
import { File as FileIcon, Upload } from 'lucide-react'
import { Button } from '../../../components/ui/button'

/** Read credentials into the existing in-memory form fields, never a separate upload. */
export function PemFileInput({
  id,
  label,
  kind,
  required,
  onChange
}: {
  id: string
  label: string
  kind: 'certificate' | 'private-key'
  required?: boolean
  onChange: (text: string) => void
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [fileName, setFileName] = useState('')
  const readerRef = useRef<FileReader | null>(null)
  const [error, setError] = useState('')
  useEffect(() => () => readerRef.current?.abort(), [])

  return (
    <div className="grid gap-1">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="border-brand/30 bg-brand-soft text-xs text-brand hover:bg-brand-soft/70 hover:text-brand"
          aria-label={`${fileName ? 'Change' : 'Select'} ${label}`}
          onClick={() => inputRef.current?.click()}
        >
          <Upload />
          {fileName ? 'Change File' : 'Select File'}
        </Button>
        {fileName && (
          <span
            className="inline-flex min-w-0 items-center gap-1.5 overflow-hidden rounded-sm border border-st-run bg-bg-elev px-2 py-0.5 font-mono text-xs text-ink"
            title={fileName}
          >
            <FileIcon className="size-3 shrink-0" />
            <span className="min-w-0 truncate">{fileName}</span>
          </span>
        )}
      </div>
      <input
        ref={inputRef}
        hidden
        id={id}
        type="file"
        aria-label={label}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        accept={kind === 'certificate' ? '.pem,.crt,.cer' : '.pem,.key'}
        required={required}
        onChange={(event) => {
          readerRef.current?.abort()
          readerRef.current = null
          onChange('')
          setError('')
          const file = event.target.files?.[0]
          setFileName(file?.name ?? '')
          if (!file) return
          if (file.size > 1024 * 1024) {
            setError('Select a PEM file smaller than 1 MB.')
            return
          }
          const reader = new FileReader()
          readerRef.current = reader
          reader.onerror = () => {
            if (readerRef.current === reader) setError('Could not read this file. Select it again.')
          }
          reader.onload = () => {
            if (readerRef.current !== reader) return
            const text = typeof reader.result === 'string' ? reader.result : ''
            const pattern =
              kind === 'certificate'
                ? /-----BEGIN CERTIFICATE-----[\s\S]+?-----END CERTIFICATE-----/
                : /-----BEGIN ((?:RSA |EC )?PRIVATE KEY)-----[\s\S]+?-----END \1-----/
            if (!pattern.test(text)) {
              setError(
                kind === 'certificate'
                  ? 'Select a PEM-encoded certificate file.'
                  : 'Select a PEM-encoded, unencrypted private key file.'
              )
              return
            }
            onChange(text)
          }
          reader.readAsText(file)
        }}
      />
      {error && (
        <p id={`${id}-error`} role="alert" className="text-xs text-st-error">
          {error}
        </p>
      )}
    </div>
  )
}
